import React, { useState, useEffect, useRef } from 'react';
import { X, ChevronLeft, ChevronRight, MapPin, Play } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

interface TourStep {
  id: string;
  title: string;
  content: string;
  target: string; // CSS selector
  position: 'top' | 'bottom' | 'left' | 'right';
  action?: {
    type: 'click' | 'hover' | 'navigate';
    value?: string;
  };
}

interface Tour {
  id: string;
  name: string;
  description: string;
  steps: TourStep[];
  category: 'getting-started' | 'features' | 'advanced';
}

const tours: Tour[] = [
  {
    id: 'getting-started',
    name: 'Getting Started',
    description: 'Learn the basics of the platform',
    category: 'getting-started',
    steps: [
      {
        id: '1',
        title: 'Welcome to the Dashboard',
        content: 'This is your main dashboard where you can see key metrics and recent activity.',
        target: '[data-tour="dashboard"]',
        position: 'bottom'
      },
      {
        id: '2',
        title: 'Navigation Sidebar',
        content: 'Use the sidebar to navigate between different sections of the application.',
        target: '[data-tour="sidebar"]',
        position: 'right'
      },
      {
        id: '3',
        title: 'Upload Your First Bill',
        content: 'Click here to upload and process your first bill.',
        target: '[data-tour="upload-bill"]',
        position: 'bottom',
        action: { type: 'click' }
      }
    ]
  },
  {
    id: 'bill-processing',
    name: 'Bill Processing',
    description: 'Learn how to process bills efficiently',
    category: 'features',
    steps: [
      {
        id: '1',
        title: 'Bills Overview',
        content: 'View all your bills and their processing status here.',
        target: '[data-tour="bills-list"]',
        position: 'top'
      },
      {
        id: '2',
        title: 'Processing Queue',
        content: 'Monitor bills in the processing queue and their progress.',
        target: '[data-tour="processing-queue"]',
        position: 'bottom'
      },
      {
        id: '3',
        title: 'Validation Results',
        content: 'Review validation results and make corrections if needed.',
        target: '[data-tour="validation-results"]',
        position: 'left'
      }
    ]
  },
  {
    id: 'advanced-features',
    name: 'Advanced Features',
    description: 'Explore AI agents and automation',
    category: 'advanced',
    steps: [
      {
        id: '1',
        title: 'AI Agents',
        content: 'Create and manage AI agents for automated processing.',
        target: '[data-tour="ai-agents"]',
        position: 'bottom'
      },
      {
        id: '2',
        title: 'Workflow Automation',
        content: 'Set up automated workflows to streamline your processes.',
        target: '[data-tour="workflow-automation"]',
        position: 'top'
      },
      {
        id: '3',
        title: 'Analytics Dashboard',
        content: 'Get insights from your data with advanced analytics.',
        target: '[data-tour="analytics"]',
        position: 'right'
      }
    ]
  }
];

interface InteractiveTourProps {
  isOpen: boolean;
  onClose: () => void;
  tourId?: string;
}

export function InteractiveTour({ isOpen, onClose, tourId }: InteractiveTourProps) {
  const [currentTour, setCurrentTour] = useState<Tour | null>(null);
  const [currentStep, setCurrentStep] = useState(0);
  const [showTourList, setShowTourList] = useState(!tourId);
  const [highlightElement, setHighlightElement] = useState<Element | null>(null);
  const overlayRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (tourId) {
      const tour = tours.find(t => t.id === tourId);
      if (tour) {
        setCurrentTour(tour);
        setShowTourList(false);
        setCurrentStep(0);
      }
    }
  }, [tourId]);

  useEffect(() => {
    if (currentTour && isOpen) {
      highlightCurrentStep();
    }
  }, [currentTour, currentStep, isOpen]);

  const highlightCurrentStep = () => {
    if (!currentTour) return;
    
    const step = currentTour.steps[currentStep];
    const element = document.querySelector(step.target);
    
    if (element) {
      setHighlightElement(element);
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };

  const startTour = (tour: Tour) => {
    setCurrentTour(tour);
    setCurrentStep(0);
    setShowTourList(false);
  };

  const nextStep = () => {
    if (currentTour && currentStep < currentTour.steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      finishTour();
    }
  };

  const prevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const finishTour = () => {
    setCurrentTour(null);
    setCurrentStep(0);
    setShowTourList(true);
    setHighlightElement(null);
  };

  const getStepPosition = () => {
    if (!highlightElement || !currentTour) return { top: '50%', left: '50%' };
    
    const rect = highlightElement.getBoundingClientRect();
    const step = currentTour.steps[currentStep];
    
    switch (step.position) {
      case 'top':
        return { top: rect.top - 20, left: rect.left + rect.width / 2 };
      case 'bottom':
        return { top: rect.bottom + 20, left: rect.left + rect.width / 2 };
      case 'left':
        return { top: rect.top + rect.height / 2, left: rect.left - 20 };
      case 'right':
        return { top: rect.top + rect.height / 2, left: rect.right + 20 };
      default:
        return { top: '50%', left: '50%' };
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50">
      {/* Overlay */}
      <div
        ref={overlayRef}
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />
      
      {/* Highlight */}
      {highlightElement && (
        <div
          className="absolute border-2 border-primary rounded-lg pointer-events-none"
          style={{
            top: highlightElement.getBoundingClientRect().top - 4,
            left: highlightElement.getBoundingClientRect().left - 4,
            width: highlightElement.getBoundingClientRect().width + 8,
            height: highlightElement.getBoundingClientRect().height + 8,
            boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.5)'
          }}
        />
      )}

      {/* Tour List */}
      {showTourList && (
        <Card className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 max-h-[80vh] overflow-y-auto">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold">Interactive Tours</h2>
              <Button variant="ghost" size="sm" onClick={onClose}>
                <X className="h-4 w-4" />
              </Button>
            </div>
            
            <p className="text-sm text-muted-foreground mb-6">
              Choose a tour to learn about different features
            </p>
            
            <div className="space-y-3">
              {tours.map((tour) => (
                <div
                  key={tour.id}
                  className="p-4 border rounded-lg hover:bg-accent cursor-pointer transition-colors"
                  onClick={() => startTour(tour)}
                >
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-medium">{tour.name}</h3>
                    <Badge variant="outline">{tour.steps.length} steps</Badge>
                  </div>
                  <p className="text-sm text-muted-foreground mb-2">{tour.description}</p>
                  <div className="flex items-center justify-between">
                    <Badge variant="secondary" className="text-xs">
                      {tour.category.replace('-', ' ')}
                    </Badge>
                    <Button size="sm" variant="outline">
                      <Play className="h-3 w-3 mr-1" />
                      Start
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tour Step */}
      {currentTour && !showTourList && (
        <Card
          className="absolute w-80 z-60"
          style={{
            top: getStepPosition().top,
            left: getStepPosition().left,
            transform: 'translate(-50%, -50%)'
          }}
        >
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <MapPin className="h-4 w-4 text-primary" />
                <span className="text-sm font-medium">{currentTour.name}</span>
              </div>
              <Button variant="ghost" size="sm" onClick={onClose}>
                <X className="h-4 w-4" />
              </Button>
            </div>
            
            <div className="mb-4">
              <h3 className="font-medium mb-2">{currentTour.steps[currentStep].title}</h3>
              <p className="text-sm text-muted-foreground">
                {currentTour.steps[currentStep].content}
              </p>
            </div>
            
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="text-xs text-muted-foreground">
                  {currentStep + 1} of {currentTour.steps.length}
                </span>
                <div className="flex gap-1">
                  {currentTour.steps.map((_, index) => (
                    <div
                      key={index}
                      className={cn(
                        "w-1.5 h-1.5 rounded-full",
                        index === currentStep ? "bg-primary" : "bg-muted"
                      )}
                    />
                  ))}
                </div>
              </div>
              
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={prevStep}
                  disabled={currentStep === 0}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <Button size="sm" onClick={nextStep}>
                  {currentStep === currentTour.steps.length - 1 ? 'Finish' : 'Next'}
                  <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// Hook for managing tour state
export function useInteractiveTour() {
  const [isOpen, setIsOpen] = useState(false);
  const [currentTourId, setCurrentTourId] = useState<string | undefined>();

  const startTour = (tourId?: string) => {
    setCurrentTourId(tourId);
    setIsOpen(true);
  };

  const closeTour = () => {
    setIsOpen(false);
    setCurrentTourId(undefined);
  };

  return {
    isOpen,
    currentTourId,
    startTour,
    closeTour
  };
}